import React from 'react';
import { Avatar, uidForTeacherId } from '../../components/Portal/Work/Avatar';
import { formatCountdown } from './todayCountdown';

/**
 * Today — the Up next card (Today redesign, Claude Design pass, 2026-10-08).
 *
 * The one thing the page is for, as the largest object on it: the next lesson
 * in the scope being looked at, with its own Start. The same lesson stays in
 * the list below, and both read from ONE row (todayCountdown hands back the
 * row it counted to), so the card and the list can never disagree.
 *
 * Presentational only. The page decides what is next (nextLessonCountdown),
 * owns the Start/Open button (one renderer for row and card, so whether a
 * lesson opens is decided once), and passes the formatted times.
 *
 * Two states, never absent while the day has lessons: the next lesson, or
 * "Done for today". An absent card reads as broken, and nobody should have to
 * hunt for whether it exists; the countdown pill it replaced kept the same
 * rule.
 */
const TodayUpNext = ({ countdown, row, loc, teacherName, isMine, timeLabel, untilLabel, startButton, onProfile }) => {
	if (!countdown) return null;
	if (!row) {
		return (
			<section className="ps-card today-next today-next--done" aria-label="Up next">
				<i className="fa-sharp fa-solid fa-circle-check" aria-hidden="true" />
				<span>Done for today: every {isMine ? 'one of your lessons' : 'lesson'} has started or been marked.</span>
			</section>
		);
	}
	// Past its time and not started: the card stays on it (todayCountdown) and
	// says so, rather than counting down to a moment that has gone.
	const inLabel = countdown.overdue ? null : formatCountdown(countdown.minutes);
	return (
		<section
			className="ps-card today-next"
			aria-label="Up next"
			// The location stripe: decoration (the `bar` tone, which carries no
			// text), so its contrast against white is irrelevant here.
			style={loc ? { '--today-next-bar': loc.bar } : undefined}>
			<div className="today-next__time">
				<span className="today-next__clock">{timeLabel}</span>
				<span className="today-next__len">{row.duration || 45} min · until {untilLabel}</span>
			</div>
			<div className="today-next__body">
				<div className="today-next__eyebrow">
					<span className="today-next__label"><i className="fa-sharp fa-solid fa-forward-step" aria-hidden="true" /> Up next</span>
					<span
						className={`ps-badge ps-badge--${countdown.tone} today-next__count`}
						title={inLabel
							? `${row.studentName || 'Next lesson'} begins in ${inLabel}`
							: `${row.studentName || 'Next lesson'} was due at ${timeLabel} and has not been started`}>
						<i className="fa-sharp fa-solid fa-clock" aria-hidden="true" /> {inLabel ? `in ${inLabel}` : 'Not started'}
					</span>
				</div>
				<p className="today-next__who" title={row.studentName}>{row.studentName}</p>
				<div className="today-next__meta">
					{row.instrument && <span>{row.instrument}</span>}
					{loc && (
						<span className="ps-badge" style={{ background: loc.fill, color: loc.text }}>
							<i className="fa-sharp fa-solid fa-location-dot" aria-hidden="true" /> {loc.name}
						</span>
					)}
					{/* Full studio only; in My lessons the teacher is you. */}
					{teacherName && (
						<span className="today-teacher__line today-next__teacher">
							<Avatar uid={uidForTeacherId(row.teacherId)} name={teacherName} size={28} className="today-teacher__face" decorative />
							<span>{teacherName}</span>
						</span>
					)}
				</div>
			</div>
			<div className="today-next__acts">
				{startButton}
				{onProfile && (
					<button type="button" className="btn-cyv-ghost today-next__profile" onClick={onProfile} title="View Student Profile">
						<i className="fa-sharp fa-solid fa-user" aria-hidden="true" /> Profile
					</button>
				)}
			</div>
		</section>
	);
};

export default TodayUpNext;
